import React, { useState, useEffect, useMemo } from "react";
import "./chatBalanceHistoryDataTable.scss";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TableToolbarSearch,
  DataTableSkeleton,
  Button,
  Pagination,
} from "@carbon/react";
import { useTranslation } from "react-i18next";
import { useGetChatBalanceHistory } from "../../hooks/useGetChatBalanceHistory";
import { clearRoute } from "../../store/routeStorage";
import { useNavigate } from "react-router-dom";
import { chatBalanceHistoryMapper } from "../../mappers/chatBalanceHistoryMapper";

export default function ChatBalanceHistoryDataTable({ guid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "name", direction: "ASC" });
  const [balanceHistory, setBalanceHistory] = useState([]);

  const {
    isPending: isBalanceHistoryPending,
    isSuccess: isBalanceHistorySuccess,
    error: BalanceHistoryError,
    data: BalanceHistoryData,
  } = useGetChatBalanceHistory({ guid });

  useEffect(() => {
    if (BalanceHistoryError) {
      clearRoute("admin");
      navigate("/error", {
        state: {
          code: BalanceHistoryError.code,
          message: BalanceHistoryError.message,
        },
      });
    }
  }, [BalanceHistoryError]);

  useEffect(() => {
    if (isBalanceHistorySuccess) {
      setBalanceHistory(BalanceHistoryData.events);
    }
  }, [isBalanceHistorySuccess, BalanceHistoryData]);

  const { headers: mappedHeaders, rows: mappedRows } = chatBalanceHistoryMapper(
    { balanceHistory, t },
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return mappedRows;

    return mappedRows.filter((row) =>
      Object.values(row).some((value) => {
        if (React.isValidElement(value)) {
          value = value.props?.children || "";
        }

        return String(value).toLowerCase().includes(search.toLowerCase());
      }),
    );
  }, [mappedRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key || sort.direction === "NONE") return filteredRows;

    const extractText = (val) => {
      if (val == null) return "";

      if (typeof val === "string" || typeof val === "number") {
        return val.toString();
      }

      if (React.isValidElement(val)) {
        return extractText(val.props?.children);
      }

      if (Array.isArray(val)) {
        return val.map(extractText).join(" ");
      }

      return "";
    };

    const parseValue = (val) => {
      const raw = extractText(val).trim();

      if (raw === "-") {
        return { type: "empty", value: null };
      }

      // Проверка: это число?
      const number = Number(raw);
      if (!isNaN(number) && raw !== "") {
        return { type: "number", value: number };
      }

      // Проверка: это дата?
      const date = Date.parse(raw);
      if (!isNaN(date)) {
        return { type: "date", value: date };
      }

      // Иначе — это строка
      return { type: "string", value: raw.toLowerCase() };
    };

    const compare = (aRaw, bRaw) => {
      const a = parseValue(aRaw);
      const b = parseValue(bRaw);

      if (a.type === b.type) {
        if (a.type === "empty") return 0;
        if (a.value === b.value) return 0;
        return a.value > b.value ? 1 : -1;
      }

      // Типы разные: приоритет — number > date > string
      const priority = { empty: 1, number: 2, date: 3, string: 4 };
      return priority[a.type] - priority[b.type];
    };

    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      const result = compare(aVal, bVal);
      return sort.direction === "ASC" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  return (
    <>
      {isBalanceHistoryPending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            {
              header: t("main.chat.chatBalanceHistory.table.frCode"),
              key: "frCode",
            },
            {
              header: t("main.chat.chatBalanceHistory.table.uuid"),
              key: "uuid",
            },
            {
              header: t("main.chat.chatBalanceHistory.table.created"),
              key: "created",
            },
            {
              header: t("main.chat.chatBalanceHistory.table.fuelDiff"),
              key: "fuelDiff",
            },
            {
              header: t("main.chat.chatBalanceHistory.table.fuelAfter"),
              key: "fuelAfter",
            },
            {
              header: t("main.chat.chatBalanceHistory.table.type"),
              key: "type",
            },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={mappedHeaders}
          rows={paginatedRows}
          isSortable
          render={({ rows, headers, getHeaderProps }) => (
            <TableContainer title={t("main.chat.chatBalanceHistory.title")}>
              <TableToolbar className="main_relations_toolbar">
                <TableToolbarSearch
                  value={search}
                  defaultExpanded={!!search}
                  persistent={!!search}
                  placeholder={`${t("main.tableText.placeholder")}`}
                  closeButtonLabelText={`${t(
                    "main.searchText.closeButtonText",
                  )}`}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                />
              </TableToolbar>
              <Table>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
                      const isSorted = sort.key === header.key;

                      const { key, ...headerProps } = getHeaderProps({
                        header,
                        onClick: () => {
                          let nextDirection;

                          if (sort.key !== header.key) {
                            nextDirection = "ASC";
                          } else if (sort.direction === "ASC") {
                            nextDirection = "DESC";
                          } else if (sort.direction === "DESC") {
                            nextDirection = "NONE";
                          } else {
                            nextDirection = "ASC";
                          }

                          setSort((prev) => ({
                            ...prev,
                            key: header.key,
                            direction: nextDirection,
                          }));
                        },
                      });
                      return (
                        <TableHeader
                          key={key}
                          {...headerProps}
                          isSortHeader={isSorted && sort.direction !== "NONE"}
                          sortDirection={isSorted ? sort.direction : "NONE"}
                        >
                          {header.header}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                backwardText={`${t("main.tableText.pagination.backwardText")}`}
                forwardText={`${t("main.tableText.pagination.forwardText")}`}
                itemsPerPageText={`${t(
                  "main.tableText.pagination.itemsPerPageText",
                )}`}
                page={pagination.page}
                onChange={({ page, pageSize }) => {
                  setPagination((prev) => ({
                    ...prev,
                    page: page,
                    pageSize: pageSize,
                  }));
                }}
                pageNumberText={`${t(
                  "main.tableText.pagination.pageNumberText",
                )}`}
                itemRangeText={(min, max, total) =>
                  `${min}–${max} ${t(
                    "main.tableText.pagination.range",
                  )} ${total} ${
                    total === 1
                      ? t("main.tableText.pagination.itemRangeText.item")
                      : t("main.tableText.pagination.itemRangeText.items")
                  }`
                }
                pageRangeText={(current, total) =>
                  `${t("main.tableText.pagination.range")} ${total} ${
                    total === 1
                      ? t("main.tableText.pagination.pageRangeText.page")
                      : t("main.tableText.pagination.pageRangeText.pages")
                  }`
                }
                pageSize={pagination.pageSize}
                pageSizes={[5, 10, 15]}
                size="md"
                totalItems={filteredRows.length}
              />
            </TableContainer>
          )}
        />
      )}
    </>
  );
}
